import { useState, useMemo } from 'react';
import { FileCode, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const MarkdownToHtml = () => {
  const [markdown, setMarkdown] = useState('');
  const [copied, setCopied] = useState(false);

  // Simple markdown to HTML converter
  const html = useMemo(() => {
    if (!markdown) return '';
    
    let result = markdown
      // Headers
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      // Bold
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.+?)__/g, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/_(.+?)_/g, '<em>$1</em>')
      // Strikethrough
      .replace(/~~(.+?)~~/g, '<del>$1</del>')
      // Code blocks
      .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code class="language-$1">$2</code></pre>')
      // Inline code
      .replace(/`(.+?)`/g, '<code>$1</code>')
      // Links
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2">$1</a>')
      // Images
      .replace(/!\[(.+?)\]\((.+?)\)/g, '<img src="$2" alt="$1" />')
      // Unordered lists
      .replace(/^\* (.+)$/gm, '<li>$1</li>')
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      // Ordered lists
      .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
      // Blockquotes
      .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
      // Horizontal rule
      .replace(/^---$/gm, '<hr />')
      .replace(/^\*\*\*$/gm, '<hr />')
      // Line breaks
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br />');
    
    // Wrap in paragraphs
    result = '<p>' + result + '</p>';
    
    // Clean up empty paragraphs
    result = result.replace(/<p><\/p>/g, '');
    
    // Wrap lists
    result = result.replace(/(<li>.*<\/li>)+/g, '<ul>$&</ul>');
    
    return result;
  }, [markdown]);

  const copyHtml = () => {
    if (html) {
      navigator.clipboard.writeText(html);
      setCopied(true);
      toast.success('HTML copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const sampleMarkdown = `# Welcome to Markdown

This is a **bold** text and this is *italic*.

## Features
- Easy to write
- Easy to read
- Converts to HTML

### Code Example
\`\`\`javascript
const hello = "world";
console.log(hello);
\`\`\`

> This is a blockquote

Visit [our website](https://example.com) for more info.

---

That's all folks!`;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <FileCode className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Markdown to HTML</h1>
          <p className="text-muted-foreground">
            Convert Markdown text to HTML with live preview
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Markdown Input */}
        <Card className="glass border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Markdown Input</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setMarkdown(sampleMarkdown)}
              >
                Load Sample
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Enter your markdown here..."
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
              className="min-h-[400px] font-mono text-sm resize-none"
            />
          </CardContent>
        </Card>

        {/* Preview and HTML Output */}
        <div className="space-y-6">
          {/* Live Preview */}
          <Card className="glass border-border/50">
            <CardHeader>
              <CardTitle>Live Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div 
                className="prose prose-sm dark:prose-invert max-w-none min-h-[150px] p-4 rounded-lg bg-muted/30"
                dangerouslySetInnerHTML={{ __html: html || '<p class="text-muted-foreground">Preview will appear here...</p>' }}
              />
            </CardContent>
          </Card>

          {/* HTML Output */}
          <Card className="glass border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>HTML Output</span>
                {html && (
                  <Button variant="ghost" size="sm" onClick={copyHtml} className="gap-2">
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    Copy
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="HTML output will appear here..."
                value={html}
                readOnly
                className="min-h-[150px] font-mono text-xs resize-none bg-muted/30"
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MarkdownToHtml;